import { useCompanyApi } from '@/api/client/api-client';
import type {
    CreateEarlyDepositErrorPayload,
    CreateEarlyDepositRequest,
    CreateEarlyDepositResponse,
    EarlyDeposit,
    EarlyDepositTableResponse,
    MergeTargetFile,
    SuggestedMergeTargetFile,
} from '@/api/interfaces/early-deposit.types';
import { notifyError, notifyWarning } from '@/bugsnag';
import { csrfToken } from '@/components/Utils/Helper';
import { isAbortError } from '@/shared/utils/errors';
import type { PreFilePayment } from '@/types/closinglock.types';
import type { AfterFetchContext } from '@vueuse/core';
import { refDebounced, useFetch } from '@vueuse/core';
import type Menu from 'primevue/menu';
import type { MenuItem } from 'primevue/menuitem';
import { computed, nextTick, reactive, ref, watch } from 'vue';

type UseFetchParams = Exclude<Parameters<typeof useFetch>[2], undefined>;

export const useExpandedRows = (() => {
    const rowIdToBoolMap: Record<EarlyDeposit['id'], boolean> = {};
    const expandedRows = ref(rowIdToBoolMap);
    const resetExpandedRows = () => {
        expandedRows.value = {};
    };
    const refreshExpandedRows = () => {
        const copy = { ...expandedRows.value };
        resetExpandedRows();
        nextTick(() => {
            expandedRows.value = copy;
        });
    };

    return () => ({
        expandedRows,
        resetExpandedRows,
        refreshExpandedRows,
    });
})();

export const useCreateEarlyDeposit = () => {
    const useFetchInstance = useFetch('/user/early-deposit', {
        immediate: false,
        beforeFetch({ options }) {
            // Add CSRF token and headers
            options.headers = {
                'X-CSRF-TOKEN': csrfToken.value,
                'Content-Type': 'application/json',
                Accept: 'application/json',
                ...options.headers,
            };
        },
    }).json<CreateEarlyDepositResponse>();

    const createEarlyDeposit = async (payload: CreateEarlyDepositRequest) => {
        await useFetchInstance.post(payload).execute();

        return {
            earlyDeposit: useFetchInstance.data,
            error: useFetchInstance.error,
            response: useFetchInstance.response,
            execute: () => useFetchInstance.post(payload).execute(),
        };
    };

    return {
        isFetchingEarlyDeposit: useFetchInstance.isFetching,
        createEarlyDeposit,
    };
};

export const useEarlyDepositsPaged = (() => {
    const initialData: EarlyDepositTableResponse = {
        data: [],
        links: {
            first: '',
            last: '',
            next: '',
            prev: '',
        },
        meta: {
            current_page: 0,
            from: 0,
            last_page: 0,
            links: [],
            path: '',
            per_page: 0,
            to: 0,
            total: 0,
        },
    };

    const tablePageNum = ref(1);
    const searchTerm = ref('');
    const openLinkFilter = ref(false);
    const highlightPreFileId = ref(new URLSearchParams(window.location.search).get('preFile') ?? '');
    const debouncedSearchTerm = refDebounced(searchTerm, 300);
    const reactivePageData = reactive({ emds: initialData.data });
    const getUrl = () => {
        const query = new URLSearchParams({
            q: debouncedSearchTerm.value,
            page: String(tablePageNum.value),
            per_page: '25',
        });
        if (openLinkFilter.value) {
            query.set('open_link', '1');
        }
        if (highlightPreFileId.value) {
            query.set('highlight', highlightPreFileId.value);
        }
        return `/user/early-deposit/table?${query}`;
    };
    const useFetchOpts = {
        initialData,
        refetch: true,
        beforeFetch({ cancel }) {
            if (!window.location.pathname.includes('/user/early-deposit')) {
                cancel();
            }
        },
        afterFetch(ctx: AfterFetchContext<EarlyDepositTableResponse>) {
            const pageResponseData = ctx.data;
            reactivePageData.emds = pageResponseData?.data ?? [];
            return ctx;
        },
        onFetchError(ctx) {
            const { error } = ctx;
            if (error && !isAbortError(error)) {
                notifyError(error, 'early-deposit.service:useEarlyDepositsPaged', {
                    url: getUrl(),
                    reactivePageData: reactivePageData.emds,
                    tablePageNum: tablePageNum.value,
                });
            }
            return ctx;
        },
    } satisfies UseFetchParams;

    const useFetchReturn = useFetch(getUrl, useFetchOpts).get().json<EarlyDepositTableResponse>();
    const lastResponseData = useFetchReturn.data;
    const earlyDepositsPage = computed(() => lastResponseData.value?.data ?? initialData.data);
    const paginatorInfo = computed(() => lastResponseData.value?.meta ?? initialData.meta);
    const emdIdToArrayIndex = computed(() => {
        return earlyDepositsPage.value.reduce<Record<EarlyDeposit['id'], number>>((acc, emd, idx) => {
            acc[emd.id] = idx;
            return acc;
        }, {});
    });

    const updateLocalEmd = (updatedEmd: Partial<EarlyDeposit>) => {
        if (!reactivePageData.emds.length) return;

        const indexInData = emdIdToArrayIndex.value[updatedEmd.id!];
        Object.assign(reactivePageData.emds[indexInData], updatedEmd);
    };

    watch(debouncedSearchTerm, () => {
        tablePageNum.value = 1;
    });

    watch(openLinkFilter, () => {
        tablePageNum.value = 1;
    });

    return () => ({
        searchTerm,
        openLinkFilter,
        highlightPreFileId,
        tablePageNum,
        reactivePageData,
        paginatorInfo,
        pageFetchError: useFetchReturn.error,
        updateLocalEmd,
        refetchCurrentPage: useFetchReturn.execute,
    });
})();

export const useEarlyDepositMenu = () => {
    const menu = ref<Menu>();
    const menuItems = ref<MenuItem[] | undefined>();

    const toggleMenu = (
        event: MouseEvent,
        paymentInfo: Pick<PreFilePayment, 'id' | 'status'>,
        emdId: EarlyDeposit['id'],
        clientId: EarlyDeposit['clients'][0]['id'],
    ) => {
        menuItems.value = getEmdActionMenu(paymentInfo, emdId, clientId);
        menu.value?.toggle(event);
    };

    return {
        menu,
        menuItems,
        toggleMenu,
    };
};

export const useDeleteClientEarlyDeposit = (() => {
    const emdRef = ref<EarlyDeposit>();
    const clientRef = ref<EarlyDeposit['clients'][number]>();

    const deleteUrl = computed(() => `/user/early-deposit/${emdRef.value?.id}/client/${clientRef.value?.id}`);

    const options: UseFetchParams = {
        immediate: false,
        beforeFetch: ({ cancel, options }) => {
            if (!emdRef.value) {
                notifyWarning(
                    'EMD not set when attempting to delete client early deposit',
                    'early-deposit.service:useDeleteClientEarlyDeposit:beforeFetch',
                );
                cancel();
            }

            if (!clientRef.value) {
                notifyWarning(
                    '[useDeleteClientEarlyDeposit] Fetch cancelled - No Client set',
                    'early-deposit.service:useDeleteClientEarlyDeposit:beforeFetch',
                );
                cancel();
            }

            options.headers = {
                'X-CSRF-TOKEN': csrfToken.value,
            };
        },
        onFetchError: ctx => {
            const { error, response } = ctx;
            notifyError(error, 'early-deposit.service:useDeleteClientEarlyDeposit', {
                emdId: emdRef.value?.id,
                clientId: clientRef.value?.id,
                responseStatus: response?.status,
            });
            return ctx;
        },
    };

    const useFetchReturn = useFetch(deleteUrl, options).delete().json<EarlyDeposit>();

    return () => ({
        data: useFetchReturn.data,
        error: useFetchReturn.error,
        isFetching: useFetchReturn.isFetching,
        response: useFetchReturn.response,
        statusCode: useFetchReturn.statusCode,
        emdRef,
        clientRef,
        runDelete: () => useFetchReturn.execute(),
        clearDeleteClientRefs: () => {
            emdRef.value = undefined;
            clientRef.value = undefined;
        },
        setDeleteClientRefs: (emd: EarlyDeposit, client: EarlyDeposit['clients'][number]) => {
            emdRef.value = emd;
            clientRef.value = client;
        },
    });
})();

export const useDeleteEarlyDeposit = (() => {
    const deleteEmdRef = ref<EarlyDeposit>();

    const getUrl = computed(() => `/user/early-deposit/${deleteEmdRef.value?.id}`);
    const options: UseFetchParams = {
        immediate: false,
        beforeFetch: ({ cancel, options }) => {
            if (!deleteEmdRef.value?.id) {
                notifyWarning(
                    '[useDeleteEarlyDeposit] Fetch cancelled - No EMD id set',
                    'early-deposit.service:useDeleteEarlyDeposit:beforeFetch',
                );
                cancel();
            }

            options.headers = {
                'X-CSRF-TOKEN': csrfToken.value,
            };
        },
        onFetchError: ctx => {
            const { error, response } = ctx;
            notifyError(error, 'early-deposit.service:useDeleteEarlyDeposit', {
                emd: deleteEmdRef.value,
                responseStatus: response?.status,
            });
            return ctx;
        },
    };
    const useFetchReturn = useFetch(getUrl, options).delete().json<EarlyDeposit>();

    return () => ({
        data: useFetchReturn.data,
        error: useFetchReturn.error,
        isFetching: useFetchReturn.isFetching,
        response: useFetchReturn.response,
        statusCode: useFetchReturn.statusCode,
        runDelete: () => useFetchReturn.execute(),
        deleteEmdRef,
        clearDeleteRef: () => {
            deleteEmdRef.value = undefined;
        },
        setDeleteRef: (emd: EarlyDeposit) => {
            deleteEmdRef.value = emd;
        },
    });
})();

export const useUpdateEarlyDeposit = (() => {
    const updateEmdRef = ref<EarlyDeposit>();
    const getUrl = computed(() => `/user/early-deposit/${updateEmdRef.value?.id}`);
    const options: UseFetchParams = {
        immediate: false,
        beforeFetch: ({ cancel, options }) => {
            if (!updateEmdRef.value?.id) {
                notifyWarning(
                    '[useUpdateEarlyDeposit] Fetch cancelled - No EMD id set',
                    'early-deposit.service:useUpdateEarlyDeposit:beforeFetch',
                );
                cancel();
            }

            options.headers = {
                'X-CSRF-TOKEN': csrfToken.value,
            };
        },
        onFetchError: ctx => {
            const { error, response } = ctx;
            const responseJSON = JSON.stringify(response?.json(), undefined, 2);
            notifyError(error, 'early-deposit.service:useUpdateEarlyDeposit', {
                emd: updateEmdRef.value,
                responseStatus: response?.status,
                responseJSON,
            });
            return ctx;
        },
    };
    const useFetchReturn = useFetch(getUrl, options).json<EarlyDeposit>();

    return () => ({
        data: useFetchReturn.data,
        error: useFetchReturn.error,
        isFetching: useFetchReturn.isFetching,
        response: useFetchReturn.response,
        statusCode: useFetchReturn.statusCode,
        runUpdate: (payload: Pick<Partial<EarlyDeposit>, 'address' | 'deposit_amount'>) => useFetchReturn.put(payload).execute(),
        updateEmdRef,
        clearUpdateRef: () => {
            updateEmdRef.value = undefined;
        },
        setUpdateRef: (emd: EarlyDeposit) => {
            updateEmdRef.value = emd;
        },
    });
})();

export const useMergePreFile = (() => {
    const mergeEmdRef = ref<EarlyDeposit>();
    const companyFetch = useCompanyApi();

    /** Best-effort suggested merge targets for an EMD; returns [] on failure. */
    const fetchSuggestedFilesForMerge = async (emdId: EarlyDeposit['id']): Promise<SuggestedMergeTargetFile[]> => {
        const { data, error } = await companyFetch(`/user/early-deposit/${emdId}/suggested-files`)
            .get()
            .json<SuggestedMergeTargetFile[]>();
        return !error.value && Array.isArray(data.value) ? data.value : [];
    };

    /** Search merge-target files by ref number; returns [] on failure. */
    const searchFiles = async (
        emdId: EarlyDeposit['id'],
        refNumber: MergeTargetFile['ref_number'],
    ): Promise<MergeTargetFile[]> => {
        const query = new URLSearchParams({ ref_number: refNumber });
        const { data, error } = await companyFetch(`/user/early-deposit/${emdId}/search-files?${query}`)
            .get()
            .json<MergeTargetFile[]>();
        return !error.value && Array.isArray(data.value) ? data.value : [];
    };

    /** Merge an EMD into a file. Returns whether it succeeded plus any server error message. */
    const mergeIntoFile = async (
        emdId: EarlyDeposit['id'],
        fileId: MergeTargetFile['id'],
    ): Promise<{ ok: boolean; message?: string }> => {
        let message: string | undefined;
        const { error } = await companyFetch(`/user/early-deposit/${emdId}/merge-into-file`, {
            onFetchError(ctx) {
                const body = ctx.data as { message?: string } | null;
                message = body?.message;
                return ctx;
            },
        })
            .post({ file_id: fileId })
            .json<{ message?: string }>();

        return { ok: !error.value, message };
    };

    return () => ({
        mergeEmdRef,
        setMergeRef: (emd: EarlyDeposit) => {
            mergeEmdRef.value = emd;
        },
        clearMergeRef: () => {
            mergeEmdRef.value = undefined;
        },
        fetchSuggestedFilesForMerge,
        searchFiles,
        mergeIntoFile,
    });
})();

export const downloadEarlyDepositClientReceipt = (paymentId: PreFilePayment['id']) => {
    return `/user/early-deposit/payment/${paymentId}/download-receipt`;
};

const useApproveEarlyDeposit = () => {
    const preFilePayment = ref<string>();
    const postUrl = computed(() => `/user/early-deposit/payment/${preFilePayment.value}/release`);
    const useFetchResources = useFetch(postUrl, {
        beforeFetch({ options }) {
            // Add CSRF token and headers
            options.headers = {
                'X-CSRF-TOKEN': csrfToken.value,
            };
        },
    }).text();
    const { refetchCurrentPage } = useEarlyDepositsPaged();

    return {
        data: useFetchResources.data,
        error: useFetchResources.error,
        isFetching: useFetchResources.isFetching,
        response: useFetchResources.response,
        statusCode: useFetchResources.statusCode,
        approveEarlyDepositPayment: (PreFilePayment: EarlyDeposit['clients'][0]['pre_file_payments'][0]['id']) => {
            preFilePayment.value = PreFilePayment;
            useFetchResources
                .post()
                .text()
                .then(() => refetchCurrentPage());
        },
    };
};

const useRejectEarlyDeposit = () => {
    const preFilePayment = ref<string>();
    const postUrl = computed(() => `/user/early-deposit/payment/${preFilePayment.value}/reject`);
    const useFetchResources = useFetch(postUrl, {
        beforeFetch({ options }) {
            // Add CSRF token and headers
            options.headers = {
                'X-CSRF-TOKEN': csrfToken.value,
            };
        },
    }).text();
    const { refetchCurrentPage } = useEarlyDepositsPaged();

    return {
        data: useFetchResources.data,
        error: useFetchResources.error,
        isFetching: useFetchResources.isFetching,
        response: useFetchResources.response,
        statusCode: useFetchResources.statusCode,
        rejectEarlyDepositPayment: (PreFilePayment: EarlyDeposit['clients'][0]['pre_file_payments'][0]['id']) => {
            preFilePayment.value = PreFilePayment;
            useFetchResources
                .post()
                .text()
                .then(() => refetchCurrentPage());
        },
    };
};

const useRerequestEarlyDeposit = () => {
    const emdId = ref<EarlyDeposit['id']>();
    const client = ref<string | number>();
    const postUrl = computed(() => `/user/early-deposit/${emdId.value}/file/${client.value}/re-notify`);

    const useFetchResources = useFetch(postUrl, {
        beforeFetch: ({ cancel, options }) => {
            if (!emdId.value) {
                notifyWarning(
                    '[useRerequestEarlyDeposit] Fetch cancelled - No EMD id set',
                    'early-deposit.service:useRerequestEarlyDeposit:beforeFetch',
                );

                cancel();
            }

            if (!client.value) {
                notifyWarning(
                    '[useRerequestEarlyDeposit] Fetch cancelled - No client id set',
                    'early-deposit.service:useRerequestEarlyDeposit:beforeFetch',
                );
                cancel();
            }

            options.headers = {
                'X-CSRF-TOKEN': csrfToken.value,
            };
        },
    }).text();

    return {
        data: useFetchResources.data,
        error: useFetchResources.error,
        isFetching: useFetchResources.isFetching,
        response: useFetchResources.response,
        statusCode: useFetchResources.statusCode,
        rerequestEarlyDeposit: (earlyDepositId: EarlyDeposit['id'], clientId: EarlyDeposit['clients'][0]['id']) => {
            emdId.value = earlyDepositId;
            client.value = clientId;
            return useFetchResources.post().text();
        },
    };
};

export const getEmdActionMenu = (
    paymentInfo: Pick<PreFilePayment, 'id' | 'status'>,
    earlyDepositId: EarlyDeposit['id'],
    clientId: EarlyDeposit['clients'][0]['id'],
) => {
    if (paymentInfo.status === 'Held') {
        return [
            {
                items: [
                    { label: 'View Deposit Info', url: downloadEarlyDepositClientReceipt(paymentInfo.id) },
                    {
                        label: 'Approve Early Deposit',
                        command: () => {
                            const approveEarlyDepositPayment = useApproveEarlyDeposit();
                            approveEarlyDepositPayment.approveEarlyDepositPayment(paymentInfo.id);
                        },
                    },
                    {
                        label: 'Reject Early Deposit',
                        command: () => {
                            const rejectEarlyDepositPayment = useRejectEarlyDeposit();
                            rejectEarlyDepositPayment.rejectEarlyDepositPayment(paymentInfo.id);
                        },
                    },
                ],
            },
        ];
    }
    if (paymentInfo.status === 'Failed') {
        return [
            {
                items: [
                    { label: 'View Deposit Info', url: downloadEarlyDepositClientReceipt(paymentInfo.id) },
                    {
                        label: 'Re-request Early Deposit',
                        command: () => {
                            const rerequestEarlyDeposit = useRerequestEarlyDeposit();
                            rerequestEarlyDeposit.rerequestEarlyDeposit(earlyDepositId, clientId);
                        },
                    },
                ],
            },
        ];
    }
};

/**
 * In an effort to speed things along for Early Deposit (EMD), the seemingly
 * similar [Create Form UI]({@link file://./../../../views/partials/user/addFile.blade.php})
 * was largely reused. Unfortunately, the server-side error validation
 * for the Create File route returns element selectors for specific parts
 * of that page. The error response for EMD was not created when that convention
 * was known, so this function translates the EMD error response into something
 * the validation messages function of the Create File route can handle. This has
 * saved us so much time.
 */
export const makeErrorResponseCompatibleWithCreateFileValidation = async (response: Response) => {
    let data: CreateEarlyDepositErrorPayload;

    try {
        data = await response.json();
    } catch (err: unknown) {
        notifyError(err as Error, 'early-deposit.service:makeErrorResponseCompatibleWithCreateFileValidation');
        return {};
    }

    if (!('errors' in data)) return {};

    const compatiblePayload: { errors: Partial<Record<string, string[]>> } = data;
    const digitRegex = /\d+/;
    const clientEmailRegex = /clients\.\d+\.email/g;
    const clientPhoneRegex = /clients\.\d+\.phone/g;
    const clientRoleRegex = /clients\.\d+\.role/g;
    const notifyingUsersRegex = /notifying_users\.\d+/g;

    for (const [errorKey, validationMsg] of Object.entries(data.errors)) {
        const idx = digitRegex.exec(errorKey)?.[0];
        if (!idx) continue;

        let compatibleKey = '';
        let compatibleMsg: string[] = [];

        if (clientEmailRegex.test(errorKey)) {
            compatibleKey = `email.${idx}`;
            compatibleMsg = validationMsg?.map(msg => msg.replaceAll(clientEmailRegex, compatibleKey)) ?? [];
        } else if (clientPhoneRegex.test(errorKey)) {
            compatibleKey = `phone.${idx}`;
            compatibleMsg = validationMsg?.map(msg => msg.replaceAll(clientPhoneRegex, compatibleKey)) ?? [];
        } else if (clientRoleRegex.test(errorKey)) {
            compatibleKey = `role.${idx}`;
            compatibleMsg = validationMsg?.map(msg => msg.replaceAll(clientRoleRegex, compatibleKey)) ?? [];
        } else if (notifyingUsersRegex.test(errorKey)) {
            compatibleKey = `notifying_users.${idx}`;
            compatibleMsg = validationMsg?.map(msg => msg.replaceAll(notifyingUsersRegex, compatibleKey)) ?? [];
        }

        if (compatibleKey) {
            delete data.errors[errorKey as keyof typeof data.errors]; // mutate the original data so thath any errors left behind may still be shown to the user
            compatiblePayload.errors[compatibleKey] = compatibleMsg;
        }
    }

    return compatiblePayload;
};
